import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Search, ShieldCheck, ShieldOff, UserX, Users } from "lucide-react";

export function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    const action = role === "admin" ? "promote" : "demote";
    if (!window.confirm(`Are you sure you want to ${action} ${u.email} ${role === "admin" ? "to admin" : "to regular user"}?`)) return;
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`${u.email} is now ${role === "admin" ? "an admin" : "a regular user"}.`);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const deleteUser = async (u) => {
    const msg =
      `Delete ${u.email}?\n\n` +
      `This will permanently remove the user account and cascade-delete:\n` +
      `  • their event reservations\n` +
      `  • their symposium registration\n` +
      `  • their active sessions\n\n` +
      `Membership applications, contact submissions, newsletter subscriptions, ` +
      `and abstract submissions are NOT linked by user_id and will remain.\n\n` +
      `This action cannot be undone. Type the email to confirm:`;
    const typed = window.prompt(msg, "");
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== u.email.toLowerCase()) {
      toast.error("Email did not match. Deletion cancelled.");
      return;
    }
    try {
      const { data } = await api.delete(`/admin/users/${u.user_id}`);
      const d = data.deleted || {};
      toast.success(
        `Deleted ${d.email}. Cascaded: ${d.reservations || 0} reservation(s), ` +
        `${d.symposium_registrations || 0} symposium registration(s), ` +
        `${d.user_sessions || 0} session(s).`
      );
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.email.toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.cell_number || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  return (
    <div data-testid="users-panel">
      <p className="eyebrow mb-2">People</p>
      <h1 className="font-serif-display text-4xl mb-2" style={{ color: "var(--ser-primary-dark)" }}>
        Users &amp; roles
      </h1>
      <p className="text-sm mb-6" style={{ color: "var(--ser-text-muted)" }}>
        Promote a registered member to admin, or revoke admin access. Admins can edit the website, manage events and reservations.
      </p>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ser-text-muted)" }} />
        <input
          type="search"
          placeholder="Search by name, email, ID or cell…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="ser-input"
          style={{ paddingLeft: "2.75rem" }}
          data-testid="admin-users-search"
        />
      </div>

      {loading ? (
        <p style={{ color: "var(--ser-text-muted)" }}>Loading…</p>
      ) : (
        <div className="ser-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: "var(--ser-sand)" }}>
                <tr className="text-left" style={{ color: "var(--ser-primary-dark)" }}>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold">Name</th>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold">Email</th>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold">Cell</th>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold">Auth</th>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold">Role</th>
                  <th className="p-3 text-xs uppercase tracking-wider font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center" style={{ color: "var(--ser-text-muted)" }}>No users match your search.</td></tr>
                )}
                {filtered.map((u) => {
                  const isSelf = currentUser?.user_id === u.user_id;
                  return (
                    <tr key={u.user_id} className="border-t" style={{ borderColor: "var(--ser-sand-dark)" }}
                      data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3">{u.name || "—"}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{u.cell_number || "—"}</td>
                      <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{u.auth_provider || "password"}</td>
                      <td className="p-3">
                        <span
                          className="ser-tag"
                          style={u.role === "admin"
                            ? { backgroundColor: "rgba(39,68,40,0.15)", color: "var(--ser-primary)" }
                            : { backgroundColor: "var(--ser-sand)", color: "var(--ser-text-muted)" }}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {isSelf ? (
                          <span className="text-xs" style={{ color: "var(--ser-text-muted)" }}>You</span>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            {u.role === "admin" ? (
                              <button onClick={() => setRole(u, "user")} className="btn-ser-ghost text-xs"
                                style={{ color: "var(--ser-clay-dark)" }}
                                data-testid={`demote-${u.user_id}`}>
                                <ShieldOff className="w-3.5 h-3.5" /> Revoke admin
                              </button>
                            ) : (
                              <button onClick={() => setRole(u, "admin")} className="btn-ser-ghost text-xs"
                                data-testid={`promote-${u.user_id}`}>
                                <ShieldCheck className="w-3.5 h-3.5" /> Make admin
                              </button>
                            )}
                            <button onClick={() => deleteUser(u)} className="btn-ser-ghost text-xs"
                              style={{ color: "var(--destructive)" }}
                              title="Delete user (cascades reservations & symposium registration)"
                              data-testid={`delete-user-${u.user_id}`}>
                              <UserX className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs mt-4" style={{ color: "var(--ser-text-muted)" }}>
        Showing {filtered.length} of {users.length} users · You cannot demote your own account.
      </p>
    </div>
  );
}

